import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Facebook, Instagram, Linkedin, Youtube, Mail, Phone, MapPin } from "lucide-react";
import SystemStatusPill from "./SystemStatusPill";
import { rejectConsent, resetConsent } from "@/lib/consent";

const SocialLink = ({ href, label, icon: Icon, size = "sm" }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid={`footer-social-${label.toLowerCase()}`}
        className={`${size === "lg" ? "w-11 h-11 bg-brand-green/90 hover:bg-brand-green text-white ring-1 ring-white/20" : "w-9 h-9 bg-white/10 hover:bg-brand-green/80 text-white/80 hover:text-white"} rounded-full flex items-center justify-center transition-colors`}
    >
        <Icon size={size === "lg" ? 18 : 15} />
    </a>
);

const Column = ({ title, children, testId }) => (
    <div data-testid={testId}>
        <h4 className="font-heading font-semibold text-sm mb-4 text-white uppercase tracking-wider">{title}</h4>
        <ul className="space-y-2.5 text-white/60 text-sm">{children}</ul>
    </div>
);

const FLink = ({ to, children, ...rest }) => (
    <li>
        <Link to={to} className="hover:text-brand-green transition-colors" {...rest}>{children}</Link>
    </li>
);

// Platform product tiles all live on /platform with anchor hashes matching product keys.
const PLATFORM_PRODUCTS = [
    { to: "/platform#passport", label: "Talent Passport™" },
    { to: "/platform#match", label: "AI Job Matching" },
    { to: "/platform#optimizer", label: "AI Resume Optimizer" },
    { to: "/platform#coach", label: "AI Career Coach" },
    { to: "/platform#verify", label: "Passport Verifications" },
    { to: "/platform#brief", label: "AI Hiring Brief™" },
];

export const Footer = () => {
    return (
        <footer className="bg-brand-navy text-white">
            <div className="container-page py-16 grid grid-cols-2 md:grid-cols-6 gap-y-10 gap-x-8">
                {/* Left brand block spans 2 cols */}
                <div className="col-span-2 md:col-span-2">
                    <Logo variant="light" size="2xl" />
                    <p className="mt-6 text-white/70 max-w-md leading-relaxed text-sm">
                        The AI-powered talent platform helping employers hire with confidence and professionals showcase verified talent through Talent Passports™, AI matching, and intelligent career tools.
                    </p>
                    <div className="mt-8">
                        <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Follow Simonara Talent</div>
                        <div className="flex items-center gap-2.5">
                            <SocialLink href="https://www.linkedin.com/company/simonara-talent-solutions" label="LinkedIn" icon={Linkedin} size="lg" />
                            <SocialLink href="https://www.instagram.com/simonaratalent" label="Instagram" icon={Instagram} />
                            <SocialLink href="https://www.facebook.com/simonaratalent" label="Facebook" icon={Facebook} />
                            <SocialLink href="https://www.youtube.com/@simonaratalent" label="YouTube" icon={Youtube} />
                        </div>
                    </div>
                </div>

                <Column title="Platform" testId="footer-col-platform">
                    {PLATFORM_PRODUCTS.map((p) => <FLink key={p.to} to={p.to}>{p.label}</FLink>)}
                </Column>

                <Column title="Solutions" testId="footer-col-solutions">
                    <FLink to="/employers">For Employers</FLink>
                    <FLink to="/talent">For Talent</FLink>
                    <FLink to="/careers">Open Roles</FLink>
                    <FLink to="/recruiting">Simonara Recruit™</FLink>
                    <FLink to="/pricing">Pricing</FLink>
                </Column>

                <Column title="Resources" testId="footer-col-resources">
                    <FLink to="/resources#blog">Blog</FLink>
                    <FLink to="/resources#help">Help Center</FLink>
                    <FLink to="/resources#faqs">FAQs</FLink>
                    <FLink to="/resources#career">Career Resources</FLink>
                    <FLink to="/resources#hiring">Hiring Resources</FLink>
                    <FLink to="/success-stories">Success Stories</FLink>
                </Column>

                <Column title="Company" testId="footer-col-company">
                    <FLink to="/about">About</FLink>
                    <FLink to="/contact">Contact</FLink>
                    <FLink to="/about#partners">Partners</FLink>
                    <li><span className="text-white/40">Careers <span className="text-[10px] uppercase tracking-wider ml-1 text-white/30">Coming soon</span></span></li>
                    <FLink to="/privacy">Privacy Policy</FLink>
                    <li>
                        <Link
                            to="/data-request"
                            onClick={() => { try { rejectConsent(); } catch (_e) {} }}
                            data-testid="footer-do-not-sell"
                            className="hover:text-brand-green transition-colors"
                        >
                            Do Not Sell or Share
                        </Link>
                    </li>
                    <FLink to="/terms">Terms</FLink>
                    <FLink to="/refund-policy">Refund Policy</FLink>
                    <FLink to="/cookies">Cookies</FLink>
                    <li>
                        <button
                            type="button"
                            onClick={() => { try { resetConsent(); window.dispatchEvent(new Event("simonara:reopen-consent")); } catch (_e) {} }}
                            data-testid="footer-cookie-preferences"
                            className="hover:text-brand-green transition-colors text-left"
                        >
                            Cookie preferences
                        </button>
                    </li>
                    <FLink to="/subprocessors">Subprocessors</FLink>
                    <FLink to="/dpa">DPA</FLink>
                    <FLink to="/trust-center">Trust Center</FLink>
                </Column>

                <Column title="Contact" testId="footer-col-contact">
                    <li className="flex items-start gap-2">
                        <Mail size={14} className="mt-0.5 text-brand-green" />
                        <a href="mailto:hello@simonaratalent.us" className="hover:text-brand-green transition-colors">hello@simonaratalent.us</a>
                    </li>
                    <li className="flex items-start gap-2">
                        <Phone size={14} className="mt-0.5 text-brand-green" />
                        <a href="tel:+13233720646" className="hover:text-brand-green transition-colors">323-372-0646</a>
                    </li>
                    <li className="flex items-start gap-2">
                        <MapPin size={14} className="mt-0.5 text-brand-green" />
                        <span className="leading-relaxed">Los Angeles, California<br /><span className="text-white/40 text-xs">Serving employers nationwide</span></span>
                    </li>
                </Column>
            </div>

            <div className="border-t border-white/10">
                <div className="container-page py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
                    <span>© {new Date().getFullYear()} Simonara Talent Solutions. All rights reserved.</span>
                    <div className="flex items-center gap-4">
                        <SystemStatusPill variant="footer" />
                        <span className="hidden lg:inline">Talent Passport™ · AI Match™ · AI Hiring Brief™</span>
                        <Link
                            to="/admin"
                            data-testid="footer-admin-link"
                            className="text-white/60 hover:text-brand-green transition-colors"
                        >
                            Admin
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
